import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import Select from "react-select";
import Sort from "components/icons/sort";

const FrontIconWrapper = styled.div`
  align-items: center;
  display: flex;
  position: absolute;
  height: 2rem;
  width: 1.25rem;
  margin-left: 0.75rem;
  ${({ isFocused, theme }) =>
    css`
      path {
        fill: ${isFocused ? theme.themeColors.primary : theme.themeColors.secondary};
      }
    `}
`;

const SortByText = styled.span`
  align-items: center;
  font-size: 0.875rem;
  font-weight: 400;
  display: flex;
  height: 2rem;
  margin-left: 2.4375rem;
  position: absolute;

  ${({ isFocused, theme }) =>
    css`
      path {
        fill: ${isFocused ? theme.themeColors.primary : theme.themeColors.secondary};
      }
    `}
`;

const OuterContainer = styled.div`
  margin: 0;
  position: relative;
  min-width: 14rem;
  height: 2rem;
`;

const StyledSelect = styled(Select)`
  font-size: 0.875rem;
  margin: 0;

  ${({ theme, highlightColor }) =>
    theme &&
    css`
      font-weight: 400;
      /* Main Outer Container */

      .gfx-select-container {
        border: none;
        margin-right: 0;
        max-height: 2rem;
        min-height: 2rem;
      }

      /* Main Inner Container */

      .gfx-select__control {
        background-color: transparent;
        border-color: ${theme.themeColors.secondary};
        border-radius: 1.3125rem;
        max-height: 2rem;
        min-height: 2rem;

        /* Input active / focus styles */

        &.gfx-select__control--menu-is-open {
          .gfx-select__indicator {
            svg {
              transform: rotate(180deg);
            }
          }
        }

        &.gfx-select__control--is-focused {
          border-color: ${theme.themeColors.primary};
          box-shadow: none;
        }
      }

      /* Selected Values on left-side container */

      .gfx-select__value-container {
        padding: 0;
        padding-left: 5.625rem;
      }

      /* Selected sort value */
      .gfx-select__single-value {
        font-size: 0.875rem;
        margin: 0;
      }

      /* Placeholder styles */

      .gfx-select__placeholder {
        font-size: 1rem;
        line-height: 1.125rem;
        color: ${theme.themeColors.secondary};
        margin-left: 0;
        font-weight: 400;
      }

      /* Vertical Line btwn input and down arrow icon */

      .gfx-select__indicator-separator {
        display: none;
      }

      /* Wrapper around down arrow icon */

      .gfx-select__indicator {
        padding-right: 0;
        display: none;
      }

      /* Selected Pill Icons */

      .gfx-select__multi-value {
        border-radius: 1.3125rem;
        background-color: ${theme.themeColors.blue1};
        margin-left: 0;
      }

      /* Selected Pill Icon Text Side */

      .gfx-select__multi-value__label {
        color: ${theme.themeColors.darkGreen1};
        font-size: ${theme.fontSize(0)};
      }

      /* Selected Pill Icon Remove "X" Side */

      .gfx-select__multi-value__remove {
        color: ${theme.themeColorShades.secondary500};
        padding-left: 0;

        svg {
          height: 1rem;
          width: 1rem;
        }

        div {
          margin: 0;
        }

        &:hover {
          background-color: initial;
          border-radius: 0 1.3125rem 1.3125rem 0;
          color: inherit;
          cursor: pointer;
        }
        transition: ${theme.opacityTransition};
      }

      /* Dropdown Outer Container */

      .gfx-select__menu {
        z-index: 2;
      }

      /* Dropdown Inner Container */

      .gfx-select__menu-list {
      }

      /* Dropdown Select Options */

      .gfx-select__option {
        color: ${theme.themeColorShades.dark800};
        /* Focused Select Option */

        &.gfx-select__option--is-focused {
          background-color: ${highlightColor === "green" ? theme.themeColors.lightGreen4 : theme.themeColors.blue1};
          cursor: pointer;
        }

        &.gfx-select__option--is-selected {
          background-color: ${theme.themeColors.primary};
          color: ${theme.colors.white};
        }
      }
    `}

  /* If error, show red input bttm border */
  ${({ hasInputError, theme }) =>
    hasInputError &&
    css`
      .gfx-select__control {
        border-color: ${theme.themeColors.orange2};
      }
    `}
`;

function SearchSortDropDown({ value, onChange, options }) {
  return (
    <OuterContainer>
      <FrontIconWrapper>
        <Sort />
      </FrontIconWrapper>
      <SortByText>Sort by:</SortByText>
      <StyledSelect
        className="gfx-select-container"
        classNamePrefix="gfx-select"
        isSearchable={false}
        options={options}
        menuPlacement="auto"
        onChange={onChange}
        value={value}
      />
    </OuterContainer>
  );
}

SearchSortDropDown.propTypes = {
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.any,
    })
  ),
  value: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.string,
    })
  ),
};

SearchSortDropDown.defaultProps = {
  onChange: () => {},
  value: "",
  options: [],
};

export default SearchSortDropDown;
